import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CheckCircle2, AlertTriangle } from "lucide-react";
import { resolveVisibility } from "./_formMeta";

const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api/public/forms`;

export default function PublicForm() {
  const { token } = useParams();
  const [form, setForm] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let cancel = false;
    axios.get(`${PUBLIC_API}/${token}`)
      .then((r) => { if (!cancel) setForm(r.data); })
      .catch(() => { if (!cancel) setError("This form is no longer available."); });
    return () => { cancel = true; };
  }, [token]);

  const visibility = useMemo(() => {
    if (!form?.definition) return {};
    return resolveVisibility(form.definition, answers);
  }, [form, answers]);

  const setVal = (key, v) => {
    setAnswers((a) => ({ ...a, [key]: v }));
    if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
  };

  const submit = async () => {
    setErrors({});
    // Local validation matches backend rules
    const localErrors = {};
    for (const [fid, s] of Object.entries(visibility)) {
      if (fid === "__key_to_id__") continue;
      const f = s.field; const v = answers[f.key];
      if (!s.visible) continue;
      if (s.required && (v == null || v === "" || (Array.isArray(v) && v.length === 0))) {
        localErrors[f.key] = "Required"; continue;
      }
      if (v == null || v === "") continue;
      if (f.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v))) {
        localErrors[f.key] = "Invalid email";
      }
      if (f.type === "mobile") {
        const m = String(v).replace(/\s/g, "");
        if (!/^(\+?91)?[6-9]\d{9}$/.test(m)) localErrors[f.key] = "Invalid Indian mobile";
      }
    }
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors); return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${PUBLIC_API}/${token}/submit`, { answers });
      setSuccess(r.data.success_message);
    } catch (e) {
      const detail = e.response?.data?.detail || "Submission failed. Please try again.";
      // Try to attach the error to a specific field if message mentions a label
      const lower = detail.toLowerCase();
      const matched = (form.definition.sections || [])
        .flatMap((s) => s.fields)
        .find((f) => lower.includes((f.label || "").toLowerCase()) || lower.includes(f.key));
      if (matched) {
        setErrors({ [matched.key]: detail.replace(/^.*?:\s*/, "") });
      } else {
        setErrors({ _global: detail });
      }
    } finally { setSubmitting(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white border border-slate-200 rounded-sm p-8 max-w-md text-center">
          <AlertTriangle size={32} className="mx-auto text-amber-500 mb-3" />
          <div className="text-lg font-semibold mb-1">Form unavailable</div>
          <div className="text-sm text-slate-500">{error}</div>
        </div>
      </div>
    );
  }
  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-8 w-8 border-2 border-[#0a2540] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white border border-slate-200 rounded-sm p-10 max-w-md text-center" data-testid="public-form-success">
          <CheckCircle2 size={40} className="mx-auto text-emerald-500 mb-3" />
          <div className="text-xl font-semibold mb-2" style={{ fontFamily: "Outfit" }}>Submitted</div>
          <div className="text-sm text-slate-600">{success}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 sm:py-12 px-4" data-testid="public-form-page">
      <div className="max-w-xl mx-auto">
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          {/* Header */}
          <div className="px-6 sm:px-8 py-6 border-b border-slate-200 bg-[#0a2540] text-white">
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>{form.title}</h1>
            {form.description && <p className="text-sm text-slate-200 mt-1.5">{form.description}</p>}
          </div>
          {/* Body */}
          <div className="px-6 sm:px-8 py-6 space-y-5">
            {(form.definition.sections || []).map((sec) => (
              <div key={sec.id}>
                {sec.title && <h2 className="text-sm font-semibold mb-3 text-slate-700">{sec.title}</h2>}
                <div className="space-y-4">
                  {(sec.fields || []).map((f) => {
                    const s = visibility[f.id];
                    if (!s?.visible) return null;
                    return (
                      <FieldRenderer
                        key={f.id}
                        field={f}
                        required={s.required}
                        value={answers[f.key]}
                        onChange={(v) => setVal(f.key, v)}
                        error={errors[f.key]}
                      />
                    );
                  })}
                </div>
              </div>
            ))}
            {errors._global && (
              <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-sm p-2.5">
                {errors._global}
              </div>
            )}
            <div className="pt-2">
              <Button
                onClick={submit}
                disabled={submitting}
                className="w-full bg-[#0a2540] hover:bg-[#0a2540]/90 h-11"
                data-testid="public-form-submit"
              >
                {submitting ? "Submitting…" : (form.definition.submit_label || "Submit")}
              </Button>
            </div>
          </div>
        </div>
        <div className="text-center text-[10px] text-slate-400 mt-4 tracking-wide uppercase">
          Powered by Griffin Pulse
        </div>
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange, required, error }) {
  const labelEl = (
    <Label className="text-xs font-semibold flex items-center gap-1">
      {field.label} {required && <span className="text-red-500">*</span>}
    </Label>
  );
  const helpEl = field.help_text ? <div className="text-[10px] text-slate-400 mt-1">{field.help_text}</div> : null;
  const errEl = error ? <div className="text-[10px] text-red-600 mt-1">{error}</div> : null;
  const wrap = (control) => (
    <div data-testid={`pub-field-${field.key}`}>
      {labelEl}
      <div className="mt-1.5">{control}</div>
      {helpEl}{errEl}
    </div>
  );

  switch (field.type) {
    case "text":
    case "email":
    case "mobile":
      return wrap(
        <Input
          type={field.type === "email" ? "email" : field.type === "mobile" ? "tel" : "text"}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          data-testid={`pub-input-${field.key}`}
          className={error ? "border-red-300" : ""}
        />
      );
    case "long_text":
      return wrap(
        <Textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ""}
          rows={4}
          data-testid={`pub-input-${field.key}`}
          className={error ? "border-red-300" : ""}
        />
      );
    case "select":
      return wrap(
        <Select value={value || ""} onValueChange={onChange}>
          <SelectTrigger data-testid={`pub-input-${field.key}`} className={error ? "border-red-300" : ""}>
            <SelectValue placeholder={field.placeholder || "Select…"} />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((o) =>
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    case "multi_select": {
      const arr = Array.isArray(value) ? value : [];
      return wrap(
        <div className="space-y-1.5" data-testid={`pub-input-${field.key}`}>
          {(field.options || []).map((o) => (
            <label key={o.value} className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={arr.includes(o.value)}
                onCheckedChange={(v) => {
                  if (v) onChange([...arr, o.value]);
                  else onChange(arr.filter((x) => x !== o.value));
                }}
                data-testid={`pub-checkbox-${field.key}-${o.value}`}
              />
              <span>{o.label}</span>
            </label>
          ))}
        </div>
      );
    }
    case "yes_no":
      return wrap(
        <div className="flex gap-2" data-testid={`pub-input-${field.key}`}>
          {["yes", "no"].map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => onChange(opt)}
              className={`px-4 py-2 rounded-sm border text-sm capitalize ${
                value === opt
                  ? "bg-[#0a2540] text-white border-[#0a2540]"
                  : "bg-white border-slate-300 hover:border-[#0a2540]"
              }`}
              data-testid={`pub-yesno-${field.key}-${opt}`}
            >
              {opt}
            </button>
          ))}
        </div>
      );
    default:
      return null;
  }
}
